import { useCallback, useRef, useState } from 'react';
import { Icon } from '#common/components/Icon.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useAudio } from './AudioContext';

interface Props {
    naturalSrc: string | null;
    cloneSrc: string | null;
}

export function VoiceDemoPlayer({ naturalSrc, cloneSrc }: Props) {
    const { t } = useEditableTranslation('hero');

    if (!naturalSrc || !cloneSrc) {
        return null;
    }

    return (
        <div className='flex flex-col gap-1 w-full'>
            <div className='flex justify-between text-sm font-bold px-2'>
                <span>{t('voice-demo.natural')}</span>
                <span>{t('voice-demo.clone')}</span>
            </div>
            <div className='w-full h-16 border-2 border-crispybluedark-700 rounded-full flex overflow-hidden relative'>
                {/* Left Half: Natural */}
                <div className='w-1/2 bg-crispyblue-900 flex items-center relative border-r-2 border-crispybluedark-700'>
                    {naturalSrc
                        ? (
                            <>
                                <div className='absolute top-0 bottom-0 right-0 left-[58px] waveform-bg' />
                                <DemoPlayButton src={naturalSrc} side='left' />
                            </>
                        )
                        : (
                            <span className='relative z-10 text-xs font-bold text-gray-500 uppercase tracking-wider mx-auto'>
                                —
                            </span>
                        )}
                </div>
                {/* Right Half: Clone */}
                <div className='w-1/2 bg-white flex items-center justify-end relative'>
                    {cloneSrc
                        ? (
                            <>
                                <div className='absolute top-0 bottom-0 left-0 right-[58px] waveform-bg-dark' />
                                <DemoPlayButton src={cloneSrc} side='right' />
                            </>
                        )
                        : (
                            <span className='relative z-10 text-xs font-bold text-gray-400 uppercase tracking-wider mx-auto'>
                                —
                            </span>
                        )}
                </div>
            </div>
        </div>
    );
}

function DemoPlayButton({ src, side }: { src: string; side: 'left' | 'right' }) {
    const { isPlayingAudio, playAudio, stopAudio } = useAudio();
    const [ playingSrc, setPlayingSrc ] = useState<string | null>(null);
    const isThisPlaying = isPlayingAudio && playingSrc === src;

    const handleClick = useCallback(() => {
        if (isThisPlaying) {
            stopAudio();
            setPlayingSrc(null);
        } else {
            playAudio(src);
            setPlayingSrc(src);
        }
    }, [ isThisPlaying, playAudio, stopAudio, src ]);

    // Reset when audio stops externally
    const prevPlaying = useRef(isPlayingAudio);
    if (prevPlaying.current && !isPlayingAudio && playingSrc) {
        setPlayingSrc(null);
    }
    prevPlaying.current = isPlayingAudio;

    return (
        <button
            type='button'
            onClick={handleClick}
            className={`relative z-10 w-10 h-10 ${
                side === 'left' ? 'ml-2' : 'mr-2'
            } bg-yellow-300 rounded-full border-2 border-black flex items-center justify-center hover:scale-105 transition cursor-pointer`}
        >
            <Icon icon={isThisPlaying ? 'fa6-solid--pause' : 'fa6-solid--play'} className='text-white text-sm' />
        </button>
    );
}
